import { Button, Form, Modal, Popconfirm, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Utils from "utils";
import MediTable from "components/Custom";
import EditableCell from "components/EditableCell";
import {
  DeleteDmdonvichuyendoi,
  getAllDmdonvichuyendoi,
  getAllDmdonvitinh,
  UpsertDmdonvichuyendoi,
} from "store/slices/sale/danhmucSlice";

const ModelChuyenDoiDonVi = ({ visibleModal, setVisibleModal, recordHH }) => {
  const { DmdonvichuyendoiList, allDmdonvitinhList, loading } = useSelector(
    (state) => state.danhMucSale
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (recordHH === null) return;
    dispatch(getAllDmdonvichuyendoi(recordHH?.mahh));
  }, [dispatch, recordHH]);

  useEffect(() => {
    dispatch(getAllDmdonvitinh({}));
  }, [dispatch]);

  const [editingKey, setEditingKey] = useState("");

  const [form] = Form.useForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isEditing = (record) =>
    record.action === "initial"
      ? record.action === editingKey
      : record.machuyendoi === editingKey;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const edit = (record) => {
    form.setFieldsValue({
      madonvichuyen: null,
      pheptinh: "*",
      tylechuyen: 1,
      ...record,
    });
    setEditingKey(
      record.action === "initial" ? record.action : record.machuyendoi
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = DmdonvichuyendoiList.slice(0);
      const index = newData.findIndex((item) =>
        item.machuyendoi ? item.machuyendoi === key : item.action === key
      );
      const item = newData[index];
      const pheptinh = row?.pheptinh ?? item?.pheptinh;
      const tylechuyen = row?.tylechuyen ?? item?.tylechuyen;
      const madonvichuyen = row?.madonvichuyen ?? item?.madonvichuyen;

      const mota = `1 ${
        allDmdonvitinhList?.find((f) => f.madonvitinh === madonvichuyen)
          ?.tendonvitinh
      } = ${pheptinh === "*" ? "" : "1/"}${tylechuyen} ${
        recordHH.tenDonViTinh
      }`;

      const payload = {
        machuyendoi: item?.machuyendoi || 0,
        mahh: recordHH?.mahh,
        madonvichuyen: madonvichuyen,
        tylechuyen: tylechuyen,
        pheptinh: pheptinh,
        mota: mota,

        ...row,
        onSuccess: () => {
          dispatch(getAllDmdonvichuyendoi(recordHH?.mahh));
          setEditingKey("");
        },
      };

      dispatch(UpsertDmdonvichuyendoi(payload));
    } catch (error) {}
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Mã",
        dataIndex: "machuyendoi",
        width: "50px",
      },
      {
        title: "Đơn vị chuyển",
        dataIndex: "madonvichuyen",
        width: "100px",
        editable: true,
        inputType: "select",
        options: allDmdonvitinhList.map((m) => ({
          value: m.madonvitinh,
          label: m.tendonvitinh,
        })),
        render: (e, r, i) =>
          allDmdonvitinhList?.find((f) => f.madonvitinh === e)?.tendonvitinh,
      },

      {
        title: "Tỷ lệ",
        dataIndex: "tylechuyen",
        editable: true,
        inputType: "number",
        align: "center",
        width: "80px",
        render: (value, record) => Utils.formatterNumber(value, "number", 2),
      },
      {
        title: "Phép tính",
        dataIndex: "pheptinh",
        width: "100px",
        editable: true,
        inputType: "select",
        options: [
          { value: "*", label: "Phép nhân" },
          { value: "/", label: "Phép chia" },
        ],
        render: (e, r, i) => r.pheptinh,
      },
      {
        title: "Mô tả",
        dataIndex: "mota",
        editable: false,
        width: "350px",
      },
      {
        fixed: "right",
        align: "center",
        width: "120px",
        render: (_, record, index) => {
          const editable = isEditing(record);
          return editable ? (
            <>
              <Button
                onClick={() => save(record?.machuyendoi || "initial")}
                className="mr-2"
                icon={<SaveOutlined />}
                shape="circle"
              />
              <Button
                onClick={() => setEditingKey("")}
                className="mr-2"
                icon={<CloseOutlined />}
                shape="circle"
              />
            </>
          ) : (
            <>
              <Button
                title={
                  record.action === "initial"
                    ? "Thêm thông tin"
                    : "Sửa thông tin"
                }
                onClick={() => edit(record)}
                className="mr-2"
                icon={
                  record.action === "initial" ? (
                    <PlusOutlined />
                  ) : (
                    <EditOutlined />
                  )
                }
                shape="circle"
              />
              {record.machuyendoi > 0 && (
                <>
                  <Popconfirm
                    title="Bạn có muốn xóa không?"
                    placement="topLeft"
                    onConfirm={() => {
                      dispatch(
                        DeleteDmdonvichuyendoi({
                          id: record.machuyendoi,
                          onSuccess: () => {
                            dispatch(getAllDmdonvichuyendoi(recordHH?.mahh));
                          },
                        })
                      );
                    }}
                  >
                    <Button
                      title={"Xóa thông tin"}
                      className="mr-2"
                      icon={<DeleteOutlined type="primary" />}
                      shape="circle"
                    />
                  </Popconfirm>
                </>
              )}
            </>
          );
        },
      },
    ],
    [allDmdonvitinhList, dispatch, edit, isEditing, recordHH?.mahh, save]
  );

  const mergedColumns = tableColumns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        title: col.title,
        dataIndex: col.dataIndex,
        editing: isEditing(record),
        inputType: col?.inputType,
        isRequired: col?.isRequired,

        options: col?.options,
      }),
    };
  });
  return (
    <Modal
      maskClosable={false}
      bodyStyle={{
        overflowY: "auto",
        height: "500px",
        maxHeight: "calc(100vh - 300px)",
      }}
      width={900}
      title={
        recordHH !== null
          ? `Thiết lập đơn vị chuyển đổi <đơn vị gốc: ${recordHH.tenDonViTinh}>`
          : ""
      }
      open={visibleModal}
      onCancel={() => {
        setVisibleModal(!visibleModal);
        form.resetFields();
      }}
      okButtonProps={
        {
          // disabled: !allowSubmit,
          // title: allowSubmit ? "Thao tác" : "Bạn không có quyền thao tác",
        }
      }
      //   onOk={() => form.submit()}
      //   okText={!isEmpty(employeeId) ? "Lưu" : "Thêm"}
    >
      <Spin tip="Đang tải..." spinning={loading}>
        <Form form={form} component={false}>
          <MediTable
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            tableColumns={mergedColumns}
            dataSource={[{ action: "initial" }].concat(
              DmdonvichuyendoiList.map((x) => ({
                ...x,
              }))
            )}
            scroll={{ x: "max-content" }}
            rowKey={(item) => item?.machuyendoi}
          />
        </Form>
      </Spin>
    </Modal>
  );
};

export default ModelChuyenDoiDonVi;
